<script>
export default {
  title: 'Project 1b: Generalized Dice Probabilities',
  starterId: null
}
</script>

<template>
  <section>

    <Readings :readings="[
      {
        title: '<cite>Mastering Matlab (1st Edition)</cite> Chapter 2'
      },
      {
        title: '<cite>Mastering Matlab (1st Edition)</cite> Chapter 3'
      },
      {
        title: '<cite>Mastering Matlab (1st Edition)</cite> Chapter 4'
      },
      {
        title: '<cite>Mastering Matlab (1st Edition)</cite> Sections 5.1 - 5.8, 5.12'
      },
      {
        title: '<cite>Mastering Matlab (1st Edition)</cite> Sections 25.1 - 25.3'
      },
      {
        title: '<cite>Mastering Matlab (1st Edition)</cite> Sections 32.1 - 32.2'
      },
    ]"/>

    <article>
      <h2>Background</h2>

      <p>As children, many of us loved to play Yahtzee and Backgammon and other games with dice. As adults we play Dungeons and Dragons (and other role-playing games) and learn about dice that have more than six sides. Now that we have the benefit of Matlab, we’d like to be able to ask questions about the probability distribution of the sum of <var>n</var> different dice, each with <var>s</var> sides. There’s a wonderful mathematical connection between this question, the coefficients of polynomials, Pascal’s triangle, the Sierpinski Sieve, Galton boards, binomial distributions, and bell curves (see <a href="http://mathworld.wolfram.com/GaltonBoard.html">mathworld.wolfram.com/GaltonBoard.html</a> for a starting point).</p>

      <p>Let’s start with the simplest possible non-trivial case. If we have a
        two-sided die (a coin!), we would expect that the probability of it landing
        on the "1" side (heads) is 0.5, the same as the probability of it landing
        on the "2" (tails) side. Similarly, if we roll a single six-sided die
        (<var>s</var>=6), we would expect a <sup>1</sup>⁄<sub>6</sub> probability
        that it would land on any of the sides 1 to 6.</p>

      <p>What if we throw <em>two</em> 2-sided dice? What would the probability
        distribution of the sum of all of the dice be? Well, there are four
        outcomes (but only three sums!), as highlighted in the table below. (You
        should fill in the blanks.) As a sanity check, we recall the sum of the
        probabilities of all outcomes must be unity.</p>

      <table class="table table-bordered text-center">
        <tr>
          <th>die #1</th>
          <th>die #2</th>
          <th>sum</th>
          <th>probability</th>
        </tr>
        <tr><td>1</td><td>1</td><td>2</td><td>___</td></tr>
        <tr><td>1</td><td>2</td><td>3</td><td>___</td></tr>
        <tr><td>2</td><td>1</td><td>3</td><td>___</td></tr>
        <tr><td>2</td><td>2</td><td>4</td><td>___</td></tr>
      </table>

      <p>What if we threw two 6-sided dice? What would the probability
        distribution of the sums be then? It gets harder to make the table.
        Fortunately, there’s a built-in Matlab function that can help us with this,
        namely <code>conv</code>:</p>
      <pre v-highlightjs><code class="plain">&gt;&gt; help conv
 CONV Convolution and polynomial multiplication.
    C = CONV(A, B) convolves vectors A and B. The resulting
    vector is length LENGTH(A)+LENGTH(B)-1.
    If A and B are vectors of polynomial coefficients, convolving
    them is equivalent to multiplying the two polynomials.
&gt;&gt; coin = [0.5 0.5]; % Distribution of a 2-sided die (coin)
&gt;&gt; conv(coin, coin)  % Note the same answer as our table above!
  ans =
     0.2500 0.5000 0.2500</code></pre>

      <p>Please note that when we throw an <var>n</var>-sided die <var>m</var> times, we need to apply the convolution function (<var>m</var> - 1) times.</p>
    </article>

    <article>
      <h2>Problem</h2>

      <p>You are to determine and plot the probability distribution of the sum of
        one through five 101-sided dice. To normalize the five cases, subtract the
        median value from each of the sums so that the values (and resulting plots)
        are all centered about 0. E.g., for the coin problem, the sums were 2, 3
        and 4 and median value was 3, so the normalized sums would be –1, 0 and 1.
        You will generate one large <var>n</var> × 6 two-dimensional array,
        where <var>n</var> is the number of different outcomes for five 101-sided
        dice. The first column in this matrix should be the normalized sums; the
        remaining columns list the probabilities of forming those sums with one
        die, with two dice, and so on up to five dice.</p>

      <table class="table table-bordered text-center">
        <tr>
          <th>
            normalized<br>
            sums
          </th>
          <th>
            probabilities for<br>
            one 101-sided die
          </th>
          <th>
            probabilities for<br>
            two 101-sided dice
          </th>
          <th>
            probabilities for<br>
            three 101-sided dice
          </th>
          <th>
            probabilities for<br>
            four 101-sided dice
          </th>
          <th>
            probabilities for<br>
            five 101-sided dice
          </th>
        </tr>
        <tr><td>-250</td><td>0</td><td>0</td><td>0</td><td>0</td><td>a very small value</td></tr>
        <tr><td>-249</td><td>0</td><td>0</td><td>0</td><td>0</td><td>a very small value</td></tr>
        <tr><td>...</td><td>...</td><td>...</td><td>...</td><td>...</td><td>...</td></tr>
        <tr><td>-1</td><td>0.00990099009901</td><td>0.00980296049407</td><td>0.00742501463165</td><td>0.00660001300591</td><td>0.00592992664476</td></tr>
        <tr><td> 0</td><td>0.00990099009901</td><td>0.00990099009901</td><td>0.00742598522179</td><td>0.00660098359606</td><td>0.00593053329928</td></tr>
        <tr><td> 1</td><td>0.00990099009901</td><td>0.00980296049407</td><td>0.00742501463165</td><td>0.00660001300591</td><td>0.00592992664476</td></tr>
        <tr><td>...</td><td>...</td><td>...</td><td>...</td><td>...</td><td>...</td></tr>
        <tr><td>250</td><td>0</td><td>0</td><td>0</td><td>0</td><td>a very small value</td></tr>
      </table>

      <p>Produce a 2D plot similar to what’s displayed in Figure 1. It will have
        all the probabilities on it simultaneously in different colors, a legend
        indicating which each one is, and each curve centered on 0 (i.e.,
        normalized). The x-axis should have the values from your leftmost column
        above. Put a title on your plot and label the axes.</p>

      <figure>
        <img src="./images/probability-distribution.jpg" alt="Probability Distribution" class="img-responsive center-block">
        <figcaption>Figure 1. Probability distribution of the sum of one to five 101-sided dice.</figcaption>
      </figure>
    </article>

    <article>
      <h2>Miscellaneous Requirements</h2>

      <p>Since you have yet to learn how to define functions and pass in
        arguments, but we still want you to write good quality code, you should
        define two constants (these would have been your function parameters),
        <var>n</var> and <var>s</var>, and a dictionary that explains what each
        means. For example:</p>
      <pre v-highlightjs><code class="plain">% DICTIONARY of constants we'll use
n = 5;   % n is the number of dice we have
s = 101; % s is the number of sides on each dice</code></pre>

      <p>One common pattern in Matlab is first to generate all the data and then
        to analyze it with different plots. You should do the same thing here:
        break your program into two different parts, the first to build the array,
        the second to generate the plot.</p>

      <p>Changing one line of your code (the "<var>s</var>=101" one) to assign
        <var>s</var> another odd value should produce a valid array and plot. For
        now, it’ll be OK to assume <var>n</var> won’t change.

      </p><p>At this stage, your code should not include any loops, <code>if</code>
      or <code>for</code> constructs. You should comment your code appropriately
      and save it in an M-file.</p>
    </article>

    <article>
      <h2>Checklist</h2>
      <ul>
        <li>A correctly working M-file, containing commented definitions for <var>n</var> and <var>s</var> and uses for them as specified.</li>
        <li>Avoidance of loops and conditional constructs.</li>
        <li>A printed listing of your labeled, titled plot.</li>
        <li>A printed listing of your M-file, with comments that describe its purpose and its inputs along with internal comments as appropriate.</li>
      </ul>
    </article>
  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/matlab/GeneralizedDiceProbabilities.vue